import { ReactNode, useCallback } from 'react';

import { SelectedCustomPage, SelectedItem } from '~/app/lib/store/types';
import Clickable, { ClickableOnClick } from '~/app/components/Clickable';
import getGenericLanguage from '~/app/lib/language/getGenericLanguage';
import { toPublicEndpoint } from '~/app/lib/getPublicEndpoint';
import { setClientCookie } from '~/app/lib/utils/cookie';
import { toBrandName } from '~/app/lib/utils/brands';
import { useSelector } from '~/app/lib/store/redux';
import { getLegalName } from '~/app/lib/privacy';
import { LANGUAGE_COOKIE_NAME } from '~/config';
import Divider from '~/app/components/Divider';
import Link from '~/app/components/Link2';
import Text from '~/app/components/Text';
import { useI18n } from '~/app/lib/i18n';
import Box from '~/app/components/Box';

import {
  BELGIUM,
  CANADA,
  DENMARK,
  LUXEMBOURG,
  NETHERLANDS,
  NORWAY,
  PORTUGAL,
  SPAIN,
  UNITED_STATES,
} from '~/app/lib/country/countryCodes';

import {
  selectUserCountry,
  selectUserLanguage,
} from '~/app/lib/store/session/selectors';

import { useItemContext } from '../ItemPageContext';
import { WithSpacingProps } from '~/app/lib/hocs/withSpacing';

export const LegalFootnote = (spacingProps: WithSpacingProps) => {
  const currentLanguage = useSelector(selectUserLanguage);
  const userCountry = useSelector(selectUserCountry);
  const itemContext = useItemContext();
  const { tx } = useI18n('item');

  const item = itemContext.data.item;
  const owners = resolveOwner(item);

  const currentLangIs = useCallback(
    (lang: string) => getGenericLanguage(currentLanguage) === lang,
    [currentLanguage]
  );

  let content: ReactNode | null = null;

  switch (userCountry) {
    case UNITED_STATES:
      {
        content = (
          <Text tag="p" lineHeight="inherit">
            {tx('presaveLegalFootnoteUs', {
              owners,
            })}
          </Text>
        );
      }

      break;
    case CANADA:
      {
        const onLangSelect: ClickableOnClick<{ lang: string }> = ({ data }) => {
          setClientCookie(LANGUAGE_COOKIE_NAME, data.lang);
          window.location.reload();
        };

        content = (
          <>
            <Box
              flexBox
              centerContent
              style={{ gap: '0.5rem', marginBottom: '1.4rem' }}
            >
              <Clickable
                isInline
                isUnderlined={!currentLangIs('en')}
                isDisabled={currentLangIs('en')}
                withDisabledStyle={false}
                onClick={onLangSelect}
                data={{ lang: 'en' }}
              >
                En
              </Clickable>
              <Divider direction="vertical" style={{ height: '1rem' }} />
              <Clickable
                isInline
                isUnderlined={!currentLangIs('fr')}
                isDisabled={currentLangIs('fr')}
                withDisabledStyle={false}
                onClick={onLangSelect}
                data={{ lang: 'fr' }}
              >
                Fr
              </Clickable>
            </Box>
            <Text lineHeight="inherit">
              <Link
                isUnderlined
                href={
                  currentLangIs('fr')
                    ? 'https://www.sonymusic.ca/fr/acces-au-compte'
                    : 'https://www.sonymusic.ca/en/account-access'
                }
              >
                {tx('presaveLegalFootnoteSmeAccountAccess')}
              </Link>
            </Text>
          </>
        );
      }

      break;

    case SPAIN:
      {
        content = (
          <Text tag="p" lineHeight="inherit">
            {getLegalName(userCountry)} tratará tus datos personales como
            responsable del tratamiento para la finalidad de enviarte
            información sobre tus artistas favoritos, así como de otros
            servicios y productos de Sony Music. Puedes ejercer tus derechos de
            protección de datos tal y como se indica en la{' '}
            <Link
              isUnderlined
              href={toPublicEndpoint(
                `/privacy/sme/${userCountry.toLowerCase()}`
              )}
            >
              Política de Privacidad
            </Link>
            .
          </Text>
        );
      }

      break;

    case PORTUGAL:
      {
        content = (
          <Text tag="p" lineHeight="inherit">
            {getLegalName(userCountry)} tratará os seus dados pessoais na
            qualidade de responsável pelo tratamento com a finalidade de lhe
            enviar informações sobre os seus artistas favoritos, bem como sobre
            outros serviços e produtos da Sony Music. Da mesma forma, caso aceda
            a uma das plataformas de streaming de música para efetuar o pre-save
            de uma faixa, álbum ou playlist de um artista, essa faixa, álbum ou
            playlist será guardado na sua biblioteca. Poderá exercer os seus
            direitos de proteção de dados conforme indicado na{' '}
            <Link
              isUnderlined
              href={toPublicEndpoint(
                `/privacy/sme/${userCountry.toLowerCase()}`
              )}
            >
              Política de Privacidade
            </Link>
            .
          </Text>
        );
      }

      break;

    case DENMARK:
    case BELGIUM:
    case NETHERLANDS:
    case LUXEMBOURG:
    case NORWAY:
      {
        content = (
          <Text tag="p" lineHeight="inherit">
            {tx('presaveLegalFootnoteDk', {
              privacyLink: ({ children }) => (
                <Link
                  isUnderlined
                  href="https://www.sonymusic.com/how-we-use-your-data/"
                >
                  {children}
                </Link>
              ),
            })}
          </Text>
        );
      }

      break;
  }

  if (!content) return null;

  return (
    <Text
      testId="presaveLegalFootnote"
      color="#999"
      size="1.2rem"
      centered
      lineHeight={1.25}
      {...spacingProps}
    >
      {content}
    </Text>
  );
};

const resolveOwner = (item: SelectedItem | SelectedCustomPage) => {
  const account = item.primaryOwnerAccount;

  const labelName = account?.name;
  const brandName = toBrandName(account?.orchardBrand);

  const owners: string[] = [];

  if ('artistName' in item) {
    owners.push(item.artistName);
  }

  if (labelName) {
    owners.push(labelName);
  }

  if (!owners.length) {
    return brandName || 'Sony Music Entertainment';
  } else if (owners.length === 1) {
    return `${owners[0]} & ${brandName}`;
  }

  return `${owners.join(', ')}, & ${brandName}`;
};
